import React, { useEffect } from "react";
import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/lib/auth";

import Landing from "@/pages/Landing";
import AuthCallback from "@/pages/AuthCallback";
import Dashboard from "@/pages/Dashboard";
import Students from "@/pages/Students";
import Teachers from "@/pages/Teachers";
import PersonProfile from "@/pages/PersonProfile";
import ScanStation from "@/pages/ScanStation";
import Kiosk from "@/pages/Kiosk";
import Reports from "@/pages/Reports";
import Direccion from "@/pages/Direccion";
import Cobranza from "@/pages/Cobranza";
import Admisiones from "@/components/app/Admisiones";
import AdmisionPublica from "@/pages/AdmisionPublica";
import Enfermeria from "@/pages/Enfermeria";
import EvaluacionesStandalone from "@/pages/EvaluacionesStandalone";
import ExpedienteAlumno from "@/pages/ExpedienteAlumno";
import Settings from "@/pages/Settings";
import Admin from "@/pages/Admin";
import OwnerObjectives from "@/pages/OwnerObjectives";
import DemoBanner from "@/components/app/DemoBanner";
import InstallAppButton from "@/components/app/InstallAppButton";
import UpdateBanner from "@/components/app/UpdateBanner";
import NfcProgram from "@/pages/NfcProgram";
import TapRegister from "@/pages/TapRegister";
import Circular from "@/pages/Circular";
import PortalPadres from "@/pages/PortalPadres";
import PortalAlumno from "@/pages/PortalAlumno";
import Comunicacion from "@/pages/Comunicacion";
import Docente from "@/pages/Docente";
import Musica from "@/pages/Musica";
import PrivacyPolicy from "@/pages/PrivacyPolicy";
import ProtectedRoute from "@/components/app/ProtectedRoute";
import AppShell from "@/components/app/AppShell";
import OwnerShell from "@/components/app/OwnerShell";
import { initNotifySound } from "@/lib/notifySound";

import "@/App.css";

function DocenteGuard({ children }) {
  const { user, loading } = useAuth();
  if (!loading && user?.role === "docente") return <Navigate to="/docente" replace />;
  return children;
}

// Detecta el portal según el dominio, para que cada app tenga su propio dominio
// (ej. uaicardaccesspadres.com muestra el Portal de Padres en la raíz, sin "/ruta").
function hostInfo() {
  const h = (typeof window !== "undefined" ? window.location.hostname : "").toLowerCase();
  const isPriv = /^(privacidad|politica|aviso)/.test(h);
  let portal = null;
  if (h.includes("padres")) portal = "padres";
  else if (h.includes("alumno")) portal = "alumno";
  else if (h.includes("docente")) portal = "docente";
  return { portal, isPriv };
}

function RootRoute() {
  const { portal, isPriv } = hostInfo();
  if (isPriv && portal) {
    const map = { padres: "padres", alumno: "alumno", docente: "docentes" };
    return <PrivacyPolicy portal={map[portal] || "general"} />;
  }
  if (portal === "padres") return <PortalPadres />;
  if (portal === "alumno") return <PortalAlumno />;
  // El acceso de docentes se realiza desde la pantalla principal (login por correo).
  return <Landing />;
}

function Shell({ children }) {
  return (
    <ProtectedRoute>
      <DocenteGuard><AppShell>{children}</AppShell></DocenteGuard>
    </ProtectedRoute>
  );
}

function OwnerArea({ children }) {
  return (
    <ProtectedRoute>
      <DocenteGuard><OwnerShell>{children}</OwnerShell></DocenteGuard>
    </ProtectedRoute>
  );
}

function AppRouter() {
  const location = useLocation();
  // Detect session_id synchronously during render (prevents race conditions)
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }
  return (
    <Routes>
      <Route path="/" element={<RootRoute />} />
      <Route path="/nfc/program/:token" element={<NfcProgram />} />
      <Route path="/tap/:token" element={<TapRegister />} />
      <Route path="/circular/:token" element={<Circular />} />
      <Route path="/padres" element={<PortalPadres />} />
      <Route path="/alumno" element={<PortalAlumno />} />
      <Route path="/privacidad" element={<PrivacyPolicy />} />
      <Route path="/privacidad-general" element={<PrivacyPolicy portal="general" />} />
      <Route path="/privacidad-padres" element={<PrivacyPolicy portal="padres" />} />
      <Route path="/privacidad-alumno" element={<PrivacyPolicy portal="alumno" />} />
      <Route path="/privacidad-docentes" element={<PrivacyPolicy portal="docentes" />} />
      <Route path="/privacidad/:portal" element={<PrivacyPolicy />} />
      <Route path="/dashboard" element={<Shell><Dashboard /></Shell>} />
      <Route path="/students" element={<Shell><Students /></Shell>} />
      <Route path="/students/:id" element={<Shell><PersonProfile /></Shell>} />
      <Route path="/teachers" element={<Shell><Teachers /></Shell>} />
      <Route path="/teachers/:id" element={<Shell><PersonProfile /></Shell>} />
      <Route path="/scan" element={<Shell><ScanStation /></Shell>} />
      <Route path="/kiosk" element={<ProtectedRoute><Kiosk /></ProtectedRoute>} />
      <Route path="/reports" element={<Shell><Reports /></Shell>} />
      <Route path="/direccion" element={<Shell><Direccion /></Shell>} />
      <Route path="/comunicacion" element={<Shell><Comunicacion /></Shell>} />
      <Route path="/musica" element={<Shell><Musica /></Shell>} />
      <Route path="/cobranza" element={<Shell><Cobranza /></Shell>} />
      <Route path="/admisiones" element={<Shell><Admisiones /></Shell>} />
      <Route path="/admision/:slug" element={<AdmisionPublica />} />
      <Route path="/enfermeria" element={<Shell><Enfermeria /></Shell>} />
      <Route path="/enfermeria/:sec" element={<Shell><Enfermeria /></Shell>} />
      <Route path="/evaluaciones" element={<EvaluacionesStandalone />} />
      <Route path="/students/:id/expediente" element={<Shell><ExpedienteAlumno /></Shell>} />
      <Route path="/settings" element={<Shell><Settings /></Shell>} />
      <Route path="/admin" element={<OwnerArea><Admin /></OwnerArea>} />
      <Route path="/admin/objetivos" element={<OwnerArea><OwnerObjectives /></OwnerArea>} />
      <Route path="/docente" element={<ProtectedRoute><Docente /></ProtectedRoute>} />
    </Routes>
  );
}

export default function App() {
  useEffect(() => { initNotifySound(); }, []);
  return (
    <div className="App">
      <AuthProvider>
        <BrowserRouter>
          <AppRouter />
          <UpdateBanner />
          <InstallAppButton />
          <DemoBanner />
          <Toaster position="top-right" richColors />
        </BrowserRouter>
      </AuthProvider>
    </div>
  );
}
